import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query'
import './auth/amplify' // configure Amplify before anything renders
import App from './App.tsx'
import { I18nProvider, useI18n } from './i18n.tsx'
import { AuthProvider } from './auth/AuthProvider.tsx'
import { Toaster, toast } from './lib/toast.tsx'
import { ConfirmHost } from './lib/confirm.tsx'
import { UpdatePrompt } from './components/UpdatePrompt.tsx'
import { ErrorBoundary } from './components/ErrorBoundary.tsx'
import { registerErrorT, errorMessage } from './lib/errors.ts'
import './index.css'

// Any mutation that throws surfaces a friendly toast (paywall, moderation, quota…).
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
  mutationCache: new MutationCache({ onError: (err) => toast.error(errorMessage(err)) }),
})

/** Bridges the i18n translator into the module-level error mapper. */
function ErrorBridge() {
  const { t } = useI18n()
  // Block body so the effect returns undefined (never registerErrorT's result).
  useEffect(() => {
    registerErrorT((k, v) => t(k, v))
  }, [t])
  return null
}

// PWA: register the app-shell service worker (production only — dev server
// module URLs must never be cached).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {})
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      {/* Top-level boundary: catches the persistent chrome (Navbar/Footer) and
          the global helpers too, so nothing can crash the app "uncaught". */}
      <ErrorBoundary>
        <ErrorBridge />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
        <Toaster />
        <ConfirmHost />
        <UpdatePrompt />
      </ErrorBoundary>
    </I18nProvider>
  </StrictMode>,
)
